import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import SideNavBar from '../components/SideNavBar';
import { getErrorMessage, getSalesAnalytics } from '../services/authApi';

function HomePage({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const data = await getSalesAnalytics();
        setAnalytics(data);
        setError('');
      } catch (requestError) {
        setError(getErrorMessage(requestError, 'Failed to load dashboard analytics.'));
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  const summary = analytics?.summary || {};
  const daily = summary.daily || {};
  const weekly = summary.weekly || {};
  const monthly = summary.monthly || {};

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-maroon-50 to-maroon-100 md:flex-row">
      <SideNavBar user={user} onLogout={onLogout} />
      <div className="flex flex-1 p-4 md:p-6">
        <div className="w-full rounded-2xl bg-white p-4 md:p-6 shadow-xl shadow-maroon-900/10">
          <h1 className="mb-1 text-3xl font-bold text-maroon-900">Dashboard</h1>
          <p className="mb-5 text-maroon-700">Welcome back{user?.name ? `, ${user.name}` : ''}.</p>

          {loading ? <p className="text-maroon-700">Loading analytics...</p> : null}
          {error ? <p className="font-medium text-red-600">{error}</p> : null}

          {!loading && !error ? (
            <>
              <div className="mb-6 grid gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-maroon-100 bg-maroon-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-maroon-700">Daily</p>
                  <p className="mt-2 text-2xl font-bold text-maroon-900">{daily.total_amount || 0}</p>
                  <p className="text-sm text-maroon-700">
                    {daily.sale_count || 0} sales | {daily.total_quantity || 0} qty
                  </p>
                  <p className="mt-1 text-sm text-green-700">Received: {daily.total_received || 0}</p>
                  <p className="text-sm text-red-600">Receivable: {daily.total_receivable || 0}</p>
                </div>
                <div className="rounded-xl border border-maroon-100 bg-maroon-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-maroon-700">Weekly</p>
                  <p className="mt-2 text-2xl font-bold text-maroon-900">{weekly.total_amount || 0}</p>
                  <p className="text-sm text-maroon-700">
                    {weekly.sale_count || 0} sales | {weekly.total_quantity || 0} qty
                  </p>
                  <p className="mt-1 text-sm text-green-700">Received: {weekly.total_received || 0}</p>
                  <p className="text-sm text-red-600">Receivable: {weekly.total_receivable || 0}</p>
                </div>
                <div className="rounded-xl border border-maroon-100 bg-maroon-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-maroon-700">Monthly</p>
                  <p className="mt-2 text-2xl font-bold text-maroon-900">{monthly.total_amount || 0}</p>
                  <p className="text-sm text-maroon-700">
                    {monthly.sale_count || 0} sales | {monthly.total_quantity || 0} qty
                  </p>
                  <p className="mt-1 text-sm text-green-700">Received: {monthly.total_received || 0}</p>
                  <p className="text-sm text-red-600">Receivable: {monthly.total_receivable || 0}</p>
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-xl border border-maroon-100 p-4">
                  <h2 className="mb-3 text-lg font-semibold text-maroon-900">Daily Report (7 days)</h2>
                  <div className="h-64 md:h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics?.charts?.daily || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line type="monotone" dataKey="total_amount" stroke="#8f213c" strokeWidth={2} />
                        <Line type="monotone" dataKey="total_received" stroke="#22c55e" strokeWidth={2} />
                        <Line type="monotone" dataKey="total_receivable" stroke="#ef4444" strokeWidth={2} />
                        <Line type="monotone" dataKey="sale_count" stroke="#cb4965" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-maroon-100 p-4">
                  <h2 className="mb-3 text-lg font-semibold text-maroon-900">Weekly Report (8 weeks)</h2>
                  <div className="h-64 md:h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics?.charts?.weekly || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar dataKey="total_amount" fill="#8f213c" />
                        <Bar dataKey="total_received" fill="#22c55e" />
                        <Bar dataKey="total_receivable" fill="#ef4444" />
                        <Bar dataKey="sale_count" fill="#cb4965" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-maroon-100 p-4">
                <h2 className="mb-3 text-lg font-semibold text-maroon-900">Monthly Report (12 months)</h2>
                <div className="h-72 md:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics?.charts?.monthly || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line type="monotone" dataKey="total_amount" stroke="#8f213c" strokeWidth={3} />
                        <Line type="monotone" dataKey="total_received" stroke="#22c55e" strokeWidth={2} />
                        <Line type="monotone" dataKey="total_receivable" stroke="#ef4444" strokeWidth={2} />
                        <Line type="monotone" dataKey="sale_count" stroke="#cb4965" strokeWidth={2} />
                      </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
